import { formatTimecode, fpsValue, type Marker, type Rational } from '@ve/core';
import { useEffect, useRef } from 'react';
import { transport } from '../../state/playback.ts';
import { tip } from '../../lib/tooltip.ts';

export interface RulerProps {
  pxPerFrame: number;
  fps: Rational;
  width: number;
  scrollLeft: number;
  viewportWidth: number;
  markers: Marker[];
  loopRange: { start: number; end: number } | null;
  loopEnabled: boolean;
}

const NICE_SECONDS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** Chooses a major tick interval (in frames) so labels are at least ~90px apart. */
export function tickInterval(pxPerFrame: number, fps: Rational): { major: number; minor: number } {
  const rate = Math.round(fpsValue(fps));
  for (const frames of [1, 2, 5, 10]) {
    if (frames * pxPerFrame >= 90 && frames < rate) return { major: frames, minor: Math.max(1, frames / 5) };
  }
  for (const s of NICE_SECONDS) {
    const frames = s * rate;
    if (frames * pxPerFrame >= 90)
      return { major: frames, minor: frames / (s === 1 ? (rate > 10 ? 5 : rate) : 5) };
  }
  return { major: 3600 * rate, minor: 600 * rate };
}

export function Ruler({
  pxPerFrame,
  fps,
  width,
  scrollLeft,
  viewportWidth,
  markers,
  loopRange,
  loopEnabled,
}: RulerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, viewportWidth);
    canvas.width = w * dpr;
    canvas.height = 26 * dpr;
    canvas.style.width = `${w}px`;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, 26);
    const { major, minor } = tickInterval(pxPerFrame, fps);
    const firstFrame = Math.floor(scrollLeft / pxPerFrame / minor) * minor;
    const lastFrame = (scrollLeft + w) / pxPerFrame;
    ctx.font = '10px ui-monospace, Menlo, monospace';
    ctx.textBaseline = 'top';
    for (let f = firstFrame; f <= lastFrame; f += minor) {
      const x = Math.round(f * pxPerFrame - scrollLeft) + 0.5;
      const isMajor = Math.abs(f / major - Math.round(f / major)) < 1e-6;
      ctx.strokeStyle = isMajor ? '#6b717b' : '#3a3f46';
      ctx.beginPath();
      ctx.moveTo(x, isMajor ? 12 : 19);
      ctx.lineTo(x, 26);
      ctx.stroke();
      if (isMajor) {
        ctx.fillStyle = '#9aa0aa';
        const tc = formatTimecode(Math.round(f), fps);
        ctx.fillText(major < Math.round(fpsValue(fps)) ? tc : tc.slice(0, 8), x + 4, 2);
      }
    }
  }, [pxPerFrame, fps, scrollLeft, viewportWidth]);

  const seekFromEvent = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    transport.seek((clientX - rect.left) / pxPerFrame);
  };

  return (
    <div
      className="ruler"
      style={{ width }}
      onPointerDown={(e) => {
        const el = e.currentTarget;
        el.setPointerCapture(e.pointerId);
        transport.pause();
        seekFromEvent(e.clientX, el);
        const move = (ev: PointerEvent) => seekFromEvent(ev.clientX, el);
        const up = () => {
          el.removeEventListener('pointermove', move);
          el.removeEventListener('pointerup', up);
        };
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up);
      }}
    >
      <canvas ref={canvasRef} className="ruler-canvas" style={{ left: scrollLeft }} />
      {loopRange && (
        <div
          className={`loop-range${loopEnabled ? ' on' : ''}`}
          style={{
            left: loopRange.start * pxPerFrame,
            width: (loopRange.end - loopRange.start) * pxPerFrame,
          }}
        />
      )}
      {markers.map((m) => (
        <div
          key={m.id}
          className="marker"
          style={{ left: m.frame * pxPerFrame, ['--marker' as string]: m.color }}
          {...tip(`${m.label || 'Marker'} · ${formatTimecode(m.frame, fps)}`)}
        />
      ))}
    </div>
  );
}
